/**
 * API Client for NorthBuilt Compass Chat Interface
 *
 * Handles communication with the streaming chat backend API.
 * Uses Server-Sent Events (SSE) for real-time token-by-token responses.
 */

import type { ClarificationResponse, Message, StreamEvent } from '../types';
import CONFIG from '../config';
import {
  createTimeoutSignal,
  debugLog,
  handleHttpError,
  handleNetworkError,
} from './api-utils';

/**
 * Response from the chat history endpoint
 */
export interface ChatHistoryResponse {
  session_id: string;
  messages: Message[];
}

/**
 * Parse API response handling both Lambda wrapped format and direct responses
 * Lambda returns { statusCode, headers, body: JSON.stringify(...) }
 * API Gateway extracts the body and sends it directly
 */
function parseApiResponse<T>(data: Record<string, unknown>): T {
  if (typeof data.body === 'string') {
    return JSON.parse(data.body) as T;
  } else if (data.body) {
    return data.body as T;
  }
  return data as T;
}

/**
 * Fetch conversation history for a session
 *
 * @param sessionId - The chat session ID
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to ChatHistoryResponse with messages array
 * @throws Error on network errors, auth errors, or HTTP errors
 */
export async function fetchChatHistory(
  sessionId: string,
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<ChatHistoryResponse> {
  // Construct endpoint URL with session ID (using streaming API endpoint)
  const baseUrl = CONFIG.REST_API_ENDPOINT.endsWith('/')
    ? CONFIG.REST_API_ENDPOINT.slice(0, -1)
    : CONFIG.REST_API_ENDPOINT;
  const endpoint = `${baseUrl}/chat/${sessionId}`;

  debugLog('Fetching chat history:', {
    endpoint,
    sessionId: sessionId.substring(0, 8) + '...',
  });

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response);

    const data = await response.json();
    const historyResponse = parseApiResponse<ChatHistoryResponse>(data);

    debugLog('Received chat history:', {
      messageCount: historyResponse.messages?.length || 0,
    });

    return historyResponse;
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Streaming timeout - longer than standard requests to accommodate LLM generation
 */
const STREAMING_TIMEOUT = 300000; // 5 minutes

/**
 * Send a chat message with streaming response
 *
 * Uses Server-Sent Events (SSE) to stream the response token by token.
 * Yields StreamEvent objects as they arrive from the server.
 *
 * Note: The streaming endpoint may return a JSON response instead of SSE
 * when clarification is needed (e.g., ambiguous client filter).
 * Check Content-Type header to determine response format.
 *
 * @param message - The user's message content
 * @param authHeader - Authorization header object from auth context
 * @param maxResults - Number of source documents to retrieve
 * @param client - Optional client filter for retrieval
 * @param sessionId - Optional session ID for conversation history
 * @param signal - Optional AbortSignal for request cancellation
 * @yields StreamEvent objects (token, sources, done, clarification, or error)
 * @throws Error on network errors, auth errors, or HTTP errors
 */
export async function* sendChatMessageStreaming(
  message: string,
  authHeader: Record<string, string>,
  maxResults: number = 5,
  client?: string,
  sessionId?: string,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, unknown> {
  // Construct streaming endpoint URL
  const baseUrl = CONFIG.REST_API_ENDPOINT.endsWith('/')
    ? CONFIG.REST_API_ENDPOINT.slice(0, -1)
    : CONFIG.REST_API_ENDPOINT;
  const endpoint = `${baseUrl}/chat`;

  // Prepare request payload
  const payload: Record<string, unknown> = {
    message: message,
    max_results: maxResults,
  };

  if (client) {
    payload.client = client;
  }

  if (sessionId) {
    payload.session_id = sessionId;
  }

  debugLog('Sending streaming chat message:', {
    endpoint,
    messageLength: message.length,
    maxResults,
    client,
    sessionId: sessionId?.substring(0, 8) + '...',
  });

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      body: JSON.stringify(payload),
      signal: createTimeoutSignal(signal, STREAMING_TIMEOUT),
    });

    await handleHttpError(response);

    // Check Content-Type to determine response format
    // JSON = clarification response, SSE = streaming response
    const contentType = response.headers.get('Content-Type') || '';

    if (contentType.includes('application/json')) {
      // Clarification response - parse as JSON and yield clarification event
      const data = await response.json();
      const parsed = parseApiResponse<ClarificationResponse>(data);

      if (parsed.type === 'clarification_needed') {
        debugLog('Received clarification from streaming endpoint:', {
          clarificationType: parsed.clarification_type,
          optionsCount: parsed.options?.length || 0,
        });
        yield {
          type: 'clarification',
          clarification: parsed,
        };
        return;
      }

      // Unexpected JSON response
      debugLog('Unexpected JSON response from streaming endpoint:', { data });
      yield {
        type: 'error',
        message: 'Unexpected response format',
        code: 'UNEXPECTED_RESPONSE',
      };
      return;
    }

    // SSE streaming response
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body available for streaming');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        debugLog('Stream ended');
        break;
      }

      // Append new data to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events (separated by double newlines)
      const events = buffer.split('\n\n');
      // Keep incomplete event in buffer
      buffer = events.pop() || '';

      for (const eventData of events) {
        if (!eventData.trim()) continue;

        // Parse SSE data lines
        for (const line of eventData.split('\n')) {
          if (line.startsWith('data: ')) {
            try {
              const event: StreamEvent = JSON.parse(line.slice(6));
              yield event;

              if (event.type === 'done') {
                debugLog('Received done event:', {
                  messageId: event.message_id,
                });
              } else if (event.type === 'error') {
                debugLog('Received error event:', { message: event.message });
              }
            } catch (parseError) {
              console.error('Failed to parse SSE event:', line, parseError);
            }
          }
        }
      }
    }
  } catch (error) {
    // Handle abort separately from network errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      debugLog('Streaming request aborted');
      yield { type: 'error', message: 'Request cancelled', code: 'ABORT' };
      return;
    }
    handleNetworkError(error);
  }
}

/**
 * Check if streaming API endpoint is configured
 * Streaming is the default and only mode when endpoint is available
 */
export function isStreamingAvailable(): boolean {
  return !!CONFIG.REST_API_ENDPOINT;
}
